import * as React from 'react';
import classnames from 'classnames';
import { MoreHorizontal } from 'lucide-react';
import { t } from '~/i18n';
import { useOutsideClick } from '~hooks/useOutsideClick';
import menu from '~styles/dropdownMenu.module.css';
import styles from './TopBarOverflow.module.css';

interface TopBarOverflowProps {
  children: React.ReactNode;
}

/** Holds the bar's secondary controls once the row runs out of width.
 *
 *  Deliberately not the account Dropdown: that menu is a list of actions with
 *  labels and icons, and these are live controls that keep their own state and
 *  appearance — the read-only chip, the sound toggles. They move into a popover
 *  rather than being rewritten as menu entries, so they read the same wherever
 *  the width puts them.
 *
 *  Only the primary axes stay in the bar at every width: which site you're
 *  viewing, and whether you're handling alarms. */
const TopBarOverflow: React.FC<TopBarOverflowProps> = ({ children }) => {
  const [open, setOpen] = React.useState(false);
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const outsideRefs = React.useMemo(() => [wrapperRef], []);
  useOutsideClick(outsideRefs, () => setOpen(false), open);

  // On the document rather than the wrapper: the controls inside are the
  // consumer's own, so focus may well be on one of them — or nowhere in here at
  // all — when the user reaches for Escape.
  React.useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  return (
    <div className={styles.Wrapper} ref={wrapperRef}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.Trigger}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={t('global.more')}
        title={t('global.more')}
        onClick={() => setOpen((o) => !o)}
      >
        <MoreHorizontal size={20} aria-hidden="true" />
      </button>

      {open && <div className={classnames(menu.Menu, menu.MenuRight, styles.Menu)}>{children}</div>}
    </div>
  );
};

export default TopBarOverflow;
